<script setup lang="ts">
import {
  formatting,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  LegacyRma,
  LegacyRmaRow,
  useContext,
  useDialogs,
  useLocalization
} from '@hantera/portal-app';
import {computed, inject, ref} from 'vue';
import ProductSelector from '../ProductSelector.vue';
import useBackend from '@/composition/useBackend';
import {CatalogSearchHit} from '@/backend/catalog';
import Decimal from 'decimal.js';
import RmaExchangeInventorySelector from "@/views/LegacyOrder/Rma/RmaExchangeInventorySelector.vue";

const props = defineProps<{
  rma: LegacyRma
  row: LegacyRmaRow
}>();

const context = useContext();
const backend = useBackend();
const dialogs = useDialogs()
const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const { currency } = formatting;
const t = useLocalization();

const imageUrl = computed(() => {
  return context.serverUrl() + '/legacy' + props.row.exchange?.article.image;
});

const canEditProduct = computed(() => {
  return props.rma.state !== 'Completed';
});

const rowBasedAdjustments = computed(() => {
  return props.row.exchange?.adjustments?.filter((a) => a.fields.some((f) => f.id === 'RowBasedAdjustment')) || [];
});

const negativeExchangeAmountWithRowBasedAdjustments = computed(() => {
  const nonRowBasedAdjustments = props.row.exchange?.adjustments.filter((a) => !rowBasedAdjustments.value.some((ra) => ra.actionId == a.actionId));
  const adjustmentTotal = nonRowBasedAdjustments?.reduce((acc, a) => acc.add(a.adjustmentTotal), new Decimal(0));
  return props.row.exchange?.exchangeTotal.sub(adjustmentTotal ?? new Decimal(0)).neg() ?? new Decimal(0);
});

function productSelected(value: CatalogSearchHit | undefined) {

  if (value && value.productNumber !== props.row.exchange?.article.articleNumber) {
    backend.legacyOrders.buildCommandGroup(
      order.value.orderId,
      'Set {productNumber} to exchange to {exchangeTo} on RMA {rmaNumber}',
      {
        productNumber: props.row.orderRow.item.articleNumber,
        exchangeTo: value.productNumber,
        rmaNumber: props.rma.rmaNumber,
      }
    )
      .command(legacyOrderCommands.upsertRmaRowExchange, {
        rmaId: props.rma.rmaId,
        rmaRowId: props.row.rmaRowId,
        exchangeArticleNumber: value.productNumber
      })
      .command(legacyOrderCommands.addActivityLog, {
        message: 'RMA {rmaNumber} - {productNumber} exchange set to {exchangeTo}',
        fields: {
          productNumber: props.row.orderRow.item.articleNumber,
          exchangeTo: value.productNumber,
          rmaNumber: props.rma.rmaNumber,
        }
      })
      .send();
  } else if (!value && props.row.exchange) {
    backend.legacyOrders.buildCommandGroup(
      order.value.orderId,
      'Remove exchange from {productNumber} on RMA {rmaNumber}',
      {
        productNumber: props.row.orderRow.item.articleNumber,
        rmaNumber: props.rma.rmaNumber,
      }
    )
      .command(legacyOrderCommands.upsertRmaRowExchange, {
        rmaId: props.rma.rmaId,
        rmaRowId: props.row.rmaRowId,
        exchangeArticleNumber: null,
        exchangeQuantity: new Decimal(0)
      })
      .command(legacyOrderCommands.addActivityLog, {
        message: 'RMA {rmaNumber} - {productNumber} exchange removed',
        fields: {
          productNumber: props.row.orderRow.item.articleNumber,
          rmaNumber: props.rma.rmaNumber,
        }
      })
      .send();
  }
}

const inventoryId = computed(() => {
  const delivery = order.value.deliveries.find(d => d.rows.find(r => r.rowId === props.row.orderRow.rowId));
  return delivery?.inventoryId ?? '';
});

const productNumber = computed(() => {
  return props.row.exchange?.article.articleNumber;
})

function selectInventory() {
  dialogs.openModal(
    'Select inventory',
    RmaExchangeInventorySelector,
    true,
    {
      ...props,
      order
    }
  )
}
</script>

<template>
  <div class="rma-exchange">
    <div class="rma-exchange__product">
      <div class="rma-exchange__image">
        <img
          :src="imageUrl"
          v-if="row.exchange"
        />
        <div
          class="rma-exchange__image-placeholder"
          v-if="!row.exchange"
        ></div>
      </div>
      <div class="rma-exchange__identity">
        <ProductSelector
          :can-edit="canEditProduct"
          :product-number="productNumber"
          :inventory-id="inventoryId"
          @selected="productSelected"
        />
        <span
          class="rma-exchange__item-name"
          v-if="row.exchange"
          :title="row.exchange.article.name"
        >{{ row.exchange.article.name }}</span>
      </div>
    </div>
    <div class="rma-exchange__values">
      <div
        class="rma-row__exchange-amount"
        v-if="rma.state !== 'Completed'"
      >
        <div class="rma-row__exchange-amount-label">Inventory</div>
        <!--      <div class="btn-editable" @click="selectInventory">-->
        <div> <!-- Editing disabled for now as it's automatic anyway -->
          {{row.exchange?.inventoryId}}
        </div>
      </div>
      <div
        class="rma-row__exchange-amount"
        :class="{
          'rma-row__exchange-amount-warning': row.exchange?.reservedQuantity?.lt(props.row.quantity),
        }"
        v-if="rma.state !== 'Completed'"
      >
        <div class="rma-row__exchange-amount-label">{{ t('orderRow.reservedQuantity') }}</div>
        <div>
          {{formatting.number(row.exchange?.reservedQuantity ?? 0)}}
        </div>
      </div>
      <div class="rma-row__exchange-amount">
        <div class="rma-row__exchange-amount-label">{{ t('orderRow.rowTotal') }}</div>
        <div class="rma-row__exchange-amount-total">{{ currency(negativeExchangeAmountWithRowBasedAdjustments, order.currencyCode) }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.rma-exchange__identity {
  display: grid;
}

.rma-exchange__item-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

</style>

<style>
.rma-exchange {
  display: grid;
  grid-template-columns: auto max-content;
  grid-auto-columns: max-content;
  column-gap: 5px;
  row-gap: 2px;
  white-space: nowrap;

  line-height: 16px;
  align-items: center;
}

.rma-exchange__product {
  display: grid;
  grid-template-columns: calc(20px + 16px) auto;
  column-gap: 5px;
  align-items: center;
}

.rma-exchange__values {
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  justify-items: end;
}

.rma-exchange__image {
  position: relative;
  grid-column: 1;
}

.rma-exchange__image-placeholder {
  border: 1px solid hsl(var(--color-10));
  height: calc(20px + 16px);
}

.rma-exchange__total {
  font-weight: bold;
}

.rma-exchange__image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.rma-row__exchange-amount {
  text-align: right;
  padding-left: 20px;
}

.rma-row__exchange-amount-label {
  white-space: nowrap;
  font-size: 12px;
  text-align: right;
  padding-left: 10px;
  color: hsl(var(--color-text-50));
}

.rma-row__exchange-amount-warning {
  background-color: var(--color-warning);
  border-radius: 4px;
  color: black;
}

@container (max-width: 550px) {
  .rma-exchange {
    grid-template-columns: auto;
    grid-template-rows: max-content max-content;
  }

  .rma-exchange__values {
    display: flex;
    justify-content: flex-end;
  }
}
</style>